import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../../../utils/reduxHelper";
import { getSearchId, triggerGa } from "../../../utils/gaUtils";
import { GA_ACTION, GA_CATEGORIES, GA_PAGE } from "../../../utils/constants";
import { determinePLPEmpty } from "../../utils/gaUtils";

export const sourcingCollectionsReduxAPI = createApi({
  reducerPath: "sourcingCollectionReduxAPI",
  baseQuery: baseQueryWithArgs,

  tagTypes: [
    "collectionDetails",
    "plpCollections",
    "collectionProducts",
    "filterSizeOptions",
    "filterColorOptions",
    "filterBrandOptions",
    "filterCategoriesOptions",
    "filterDiscountOptions",
    "filterTagsOptions",
    "filterGenderOptions",
    "filterTrendOptions",
    "DropOffSurveyStatus",
    "IntentCaptureStatus",
    "attributeFilters",
    "heroTrends",
    "trendLandingMeta",
    "plpBreakTrends",
  ],

  endpoints: (builder) => ({
    //GET Queries
    getCollectionDetails: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getCollectionDetails?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["collectionDetails"],
      keepUnusedDataFor: 60,
    }),
    getPLPCollections: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(`s/getPLPCollections?brandId=${brandId}`),
      providesTags: ["plpCollections"],
      keepUnusedDataFor: 300,
    }),
    /** SEL-1328: PLP hero carousel — top admin-ranked "live" trends. */
    getSourcingTrendsHero: builder.query({
      // No client-side count: X hero trends is whatever the admin has put live on
      // Hero (PRD "X set from admin"); the backend caps only as a safety.
      query: ({ limit } = {}) =>
        getQueryStringWithCreator(
          Number(limit) > 0
            ? `s/getSourcingTrendsHero?limit=${limit}`
            : "s/getSourcingTrendsHero"
        ),
      transformResponse: (res) => {
        const trends = res?.data?.trends || [];
        return {
          enabled: res?.data?.trends_enabled === true,
          trends: trends.map((t) => ({
            trendId: t.trend_id,
            trendName: t.trend_name,
            trendType: t.trend_type,
            rank: t.rank ?? null,
            heroGraphicUrl: t.hero_graphic_url,
          })),
        };
      },
      providesTags: ["heroTrends"],
      keepUnusedDataFor: 300,
    }),
    /** SEL-1345: Trend Landing Page header — title + chosen tags/metrics. */
    getSourcingTrendLandingMeta: builder.query({
      query: ({ trendId }) =>
        getQueryStringWithCreator(
          `s/getSourcingTrendLandingMeta?trendId=${trendId}`
        ),
      transformResponse: (res) => {
        const trend = res?.data?.trend;
        if (!trend) return null;
        return {
          trendId: trend.trend_id,
          trendName: trend.trend_name,
          rank: trend.rank ?? null,
          trendType: trend.trend_type || "",
          liveSurface: trend.live_surface || "off",
          tags: trend.tags || [],
          metrics: trend.metrics || {},
        };
      },
      providesTags: ["trendLandingMeta"],
      keepUnusedDataFor: 300,
    }),
    /** PLP-break trend teaser cards, inserted every 4th product into the grid.
     * Carries the PLP's filter context so breaks follow the filtered journey
     * (PRD: filtered to the selected values, >20 products after filtering). */
    getSourcingTrendsPlp: builder.query({
      query: ({
        limit = 20,
        filters,
        attributeFilters,
        categoryId = 0,
        subcategoryId = 0,
      } = {}) => {
        let url = `s/getSourcingTrendsPlp?limit=${limit}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (attributeFilters && Object.keys(attributeFilters).length > 0)
          url += `&attributeFilters=${encodeURIComponent(
            JSON.stringify(attributeFilters)
          )}`;
        if (Number(categoryId) > 0) url += `&categoryId=${categoryId}`;
        if (Number(subcategoryId) > 0) url += `&subcategoryId=${subcategoryId}`;
        return getQueryStringWithCreator(url);
      },
      transformResponse: (res) => {
        const trends = res?.data?.trends || [];
        return trends.map((t) => ({
          trendId: t.trend_id,
          trendName: t.trend_name,
          trendType: t.trend_type || "",
          eyebrow: t.eyebrow,
          tags: t.tags || [],
          metrics: t.metrics || {},
          videoUrl: t.video_url,
          creatorsCount: t.creators_count ?? 0,
          creatorAvatarUrls: t.creator_avatar_urls || [],
          // Raw ProductInfo-shaped array — mapped via buildSimilar at the
          // point of rendering (see getTrendProductsForPdp for precedent).
          products: t.products || [],
        }));
      },
      providesTags: ["plpBreakTrends"],
      keepUnusedDataFor: 300,
    }),
    getCollectionProducts: builder.query({
      query: ({
        collectionId,
        brandId,
        categoryId = 0,
        subcategoryId,
        sortBy,
        search,
        filters,
        page,
        attributeFilters = {},
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        const searchText = encodeURIComponent(search);
        const filter = encodeURIComponent(JSON.stringify(filters));
        const attrFilters = encodeURIComponent(
          JSON.stringify(attributeFilters)
        );
        const plpPart =
          plpCollectionId && Number(plpCollectionId) > 0
            ? `&plpCollectionId=${plpCollectionId}`
            : "";
        const trendPart =
          trendId && Number(trendId) > 0 ? `&trendId=${trendId}` : "";
        return getQueryStringWithCreator(
          `s/getCollectionProducts?collectionId=${collectionId}&brandId=${brandId}&categoryId=${categoryId}&subcategoryId=${subcategoryId}&sortBy=${sortBy}&search=${searchText}&filters=${filter}&attributeFilters=${attrFilters}&page=${page}&limit=20${plpPart}${trendPart}`
        );
      },
      transformResponse: (response, meta, arg) => {
        const search = (arg?.search || "").trim();
        if (
          arg?.variant === "brand-sourcing" &&
          (arg?.page || 1) === 1 &&
          search &&
          response?.success &&
          arg?.searchId &&
          arg.searchId === getSearchId("brand")
        ) {
          triggerGa({
            category: GA_CATEGORIES.USER_INTERACTION,
            action: GA_ACTION.SEARCH_RESULTS_LOAD,
            label: search,
            label2: String(response?.num_pages ?? ""),
            page: GA_PAGE.BRAND_SOURCING,
            screenName: determinePLPEmpty(
              new URLSearchParams(window.location.search)
            ),
            eventValue:
              response?.total_count ?? response?.products?.length ?? 0,
            collection_id: Number(arg?.collectionId) || 0,
            searchSurface: "brand",
          });
        }
        response.pages = [response.products];
        return response;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          brandId,
          collectionId,
          filters,
          search,
          sortBy,
          subcategoryId,
          categoryId = 0,
          attributeFilters = {},
          plpCollectionId = 0,
          trendId = 0,
          searchId = "",
        } = queryArgs;
        return `endpointName=${endpointName}&brandId=${brandId}&collectionId=${collectionId}&filters=${JSON.stringify(
          filters
        )}&search=${search}&searchId=${searchId}&sortBy=${sortBy}&subcategoryId=${subcategoryId}&categoryId=${categoryId}&attributeFilters=${JSON.stringify(
          attributeFilters
        )}&plpCollectionId=${plpCollectionId || 0}&trendId=${trendId || 0}`;
      },
      merge: (currentCache, newItems, otherArgs) => {
        const {
          arg: { page },
        } = otherArgs;
        if (currentCache.pages === undefined) currentCache.pages = [];
        const products = Array.isArray(newItems?.products)
          ? newItems.products
          : [];
        currentCache.pages[page - 1] = [...products];
      },
      // Keep loaded pages alive across a PDP visit so back-navigation restores
      // the full list without refetching (default 60s is shorter than a
      // typical PDP browse).
      keepUnusedDataFor: 600,
      forceRefetch({ currentArg, previousArg }) {
        // Fetch only when the requested page actually changes. Everything
        // else that matters (sort/filter/search/collection) is part of the
        // cache key, so changing it creates a fresh entry that fetches on its
        // own. Comparing object references here (`currentArg !== previousArg`)
        // is always true — args are rebuilt every render — which caused a
        // pointless refetch on every back-navigation from the PDP.
        return currentArg?.page !== previousArg?.page;
      },
    }),
    getFilterSizeOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterSizeOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterSizeOptions"],
    }),
    getFilterColorOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterColorOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterColorOptions"],
    }),
    getFilterBrandOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterBrandOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterBrandOptions"],
    }),
    getFilterCategoriesOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterCategoriesOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterCategoriesOptions"],
    }),
    getFilterDiscountOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId, filters }) => {
        let url = `s/getFilterDiscountOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterDiscountOptions"],
    }),
    getFilterTagsOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterTagOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterTagsOptions"],
    }),
    /** PRD Filters: live trends as a filter value, counts scoped like every facet. */
    getFilterTrendOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterTrendOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterTrendOptions"],
    }),
    getFilterGenderOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        let url = `s/getFilterHeaderOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterGenderOptions"],
    }),

    // ─── Drop-off survey ──────────────────────────────────────────────────────
    submitDropOffSurvey: builder.mutation({
      query: (body) => ({
        url: "s/submitDropOffSurvey",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getDropOffSurveyStatus: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(
          `s/getDropOffSurveyStatus?brand_id=${brandId}`
        ),
      providesTags: ["DropOffSurveyStatus"],
    }),

    // ─── Intent-capture form (SEL-823) ────────────────────────────────────────
    submitIntentCapture: builder.mutation({
      query: (body) => ({
        url: "s/submitIntentCapture",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getIntentCaptureStatus: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(
          `s/getIntentCaptureStatus?brand_id=${brandId}`
        ),
      providesTags: ["IntentCaptureStatus"],
    }),
    getAttributeFilters: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
        trendId = 0,
      }) => {
        const filter = filters
          ? encodeURIComponent(JSON.stringify(filters))
          : encodeURIComponent("[]");
        let url = `s/getAttributeFilters?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}&filters=${filter}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        if (Number(trendId) > 0) url += `&trendId=${trendId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["attributeFilters"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetCollectionDetailsQuery,
  useGetPLPCollectionsQuery,
  useGetSourcingTrendsHeroQuery,
  useGetSourcingTrendLandingMetaQuery,
  useGetSourcingTrendsPlpQuery,
  useLazyGetCollectionProductsQuery,
  useLazyGetFilterSizeOptionsQuery,
  useLazyGetFilterColorOptionsQuery,
  useLazyGetFilterBrandOptionsQuery,
  useLazyGetFilterCategoriesOptionsQuery,
  useLazyGetFilterDiscountOptionsQuery,
  useLazyGetFilterTagsOptionsQuery,
  useLazyGetFilterTrendOptionsQuery,
  useLazyGetFilterGenderOptionsQuery,
  useLazyGetAttributeFiltersQuery,
  useGetCollectionProductsQuery,
  useSubmitDropOffSurveyMutation,
  useGetDropOffSurveyStatusQuery,
  useSubmitIntentCaptureMutation,
  useGetIntentCaptureStatusQuery,
} = sourcingCollectionsReduxAPI;
